// useGuestList — the hand-written per-domain hook over the generated client. It lives
// under app/hooks/ (the sanctioned wrapper dir, lint-exempt), so feature screens use IT and never raw
// useQuery/useMutation. It builds on useBandQuery/useBandMutation, so the query key is ALWAYS
//   guestListKey(bandId, showId) = ['band', bandId, 'show', showId, 'guests']
// — the EXACT key the invalidation registry already invalidates on guests-changed/guest-scanned, so a
// live add/scan repaints automatically once a screen mounts show:{id} (useLiveGroup) and reads this hook.
//
// Optimistic rules: add/remove are in the proven-safe optimistic set (onMutate snapshot →
// rollback+toast onError → invalidate onSettled); scan is server-confirm THEN repaint (not optimistic). Every
// mutation surfaces the server error verbatim and NEVER reports success on an unpersisted write.
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { guestListKey } from "../realtime/invalidationRegistry.js";
import { pushToast } from "../toasts.js";

type GuestList = components["schemas"]["GuestList"];
type GuestRow = components["schemas"]["GuestRow"];
type AddGuestRequest = components["schemas"]["AddGuestRequest"];

/** Rollback context carried onMutate → onError for the optimistic mutations. */
interface GuestListSnapshot {
  previous?: GuestList;
}

/**
 * Restore the cache to its pre-mutation snapshot. When there was NO cached list (`previous` undefined — e.g.
 * an add attempted while the initial GET was still in flight), `setQueryData(key, undefined)` is a no-op in
 * TanStack, which would strand the optimistic phantom row; so we `removeQueries` to drop it and let the active
 * observer refetch truth (no phantom success, even on the not-yet-loaded path).
 */
function rollback(qc: QueryClient, key: readonly unknown[], previous: GuestList | undefined): void {
  if (previous) qc.setQueryData(key, previous);
  else void qc.removeQueries({ queryKey: key });
}

// ---- counts ---------------------------------------------------------------------------------

/** Seats one row contributes: 1 (the guest) + their plus-ones. */
export function guestSeats(row: GuestRow): number {
  return 1 + (row.plusCount ?? 0);
}

/** Σ(1 + plusCount) over rows — the comps formula, used to keep the optimistic count consistent.*/
export function sumSeats(rows: readonly GuestRow[]): number {
  return rows.reduce((n, r) => n + guestSeats(r), 0);
}

export interface GuestCounts {
  /** compsCount = Σ(1 + plusCount) over non-deleted rows — SCAN-INDEPENDENT. Does not move on scan.*/
  comps: number;
  /** Arrived HEADS = Σ(1 + plusCount) over scanned rows — the live counter demo step 2 watches (a DIFFERENT number). */
  arrived: number;
}

/**
 * Door/desktop counts derived from the list. `comps` reads the server's scan-independent figure; `arrived`
 * is the live scanned-heads counter. Deliberately kept distinct so the comps figure never wires to scan
 * (the Phase-1 trap, step 2).
 */
export function guestCounts(list: GuestList | undefined): GuestCounts {
  const rows = list?.items ?? [];
  const scanned = rows.filter((r) => r.scannedOn != null);
  return {
    comps: list?.compsCount ?? sumSeats(rows),
    arrived: sumSeats(scanned),
  };
}

/** True for an optimistic (not-yet-persisted) row — the temp guestId sentinel (rendered in a pending state). */
export function isPendingGuest(row: GuestRow): boolean {
  return typeof row.guestId === "string" && row.guestId.startsWith("optimistic-");
}

// ---- query --------------------------------------------------------------------------------------------

/** GET the show's guest list. 403 (Content) / 404 (forged show) surface as the query's error state.*/
export function useGuestList(showId: string) {
  const { api } = useApp();
  return useBandQuery<GuestList>(
    ["show", showId, "guests"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/guests",
        { params: { path: { bandId, showId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000 }, // schedule-domain freshness
  );
}

export type GuestPartnerOptions = components["schemas"]["GuestPartnerOptions"];
export type GuestPartnerOption = components["schemas"]["GuestPartnerOption"];

/**
 * The partners the caller may comp on this show (ART-239 part 3): their own (`scope: "own"`, one "+ Bring partner"
 * chip) or every member's for an AV / Tour Manager (`scope: "all"`). A CHILD of the guest-list key, so an add or
 * remove here (which invalidates the list prefix) and a `guests-changed` signal both refresh each `onList`.
 * Only asked when the seat may add guests — the route is guests:write.
 */
export function useGuestPartners(showId: string, enabled = true) {
  const { api } = useApp();
  return useBandQuery<GuestPartnerOptions>(
    ["show", showId, "guests", "partners"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/guests/partners",
        { params: { path: { bandId, showId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000, enabled },
  );
}

// ---- mutations ----------------------------------------------------------------------------------------

/** Add a guest — optimistic with rollback+toast.*/
export function useAddGuest(showId: string) {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = guestListKey(bandId, showId);

  return useBandMutation<GuestRow, AddGuestRequest, GuestListSnapshot>(
    async (band, body) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/guests",
        { params: { path: { bandId: band, showId } }, body },
      );
      // No phantom success: a non-2xx or a body without the created row throws → onError rolls
      // back + toasts, and the caller's mutation never resolves "success".
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onMutate: async (body) => {
        await qc.cancelQueries({ queryKey: key });
        const previous = qc.getQueryData<GuestList>(key);
        const optimistic: GuestRow = {
          guestId: `optimistic-${crypto.randomUUID()}`,
          name: body.name ?? "",
          plusCount: body.plusCount ?? 0,
          note: body.note ?? null,
          source: "manual",
          scannedOn: null,
          scannedByPersonId: null,
          addedByPersonId: null,
          createdOn: new Date().toISOString(),
        };
        qc.setQueryData<GuestList>(key, (old) => {
          const items = [...(old?.items ?? []), optimistic];
          return { items, compsCount: sumSeats(items) };
        });
        return { previous };
      },
      onError: (err, _body, ctx) => {
        rollback(qc, key, ctx?.previous);
        pushToast(errorMessage(err, "Couldn't add guest"));
      },
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    },
  );
}

/** Remove a guest by id — optimistic with rollback+toast.*/
export function useRemoveGuest(showId: string) {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = guestListKey(bandId, showId);

  return useBandMutation<void, string, GuestListSnapshot>(
    async (band, guestId) => {
      const { error, response } = await api.DELETE(
        "/api/v1/bands/{bandId}/shows/{showId}/guests/{guestId}",
        { params: { path: { bandId: band, showId, guestId } } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onMutate: async (guestId) => {
        await qc.cancelQueries({ queryKey: key });
        const previous = qc.getQueryData<GuestList>(key);
        qc.setQueryData<GuestList>(key, (old) => {
          if (!old) return old;
          const items = (old.items ?? []).filter((r) => r.guestId !== guestId);
          return { items, compsCount: sumSeats(items) };
        });
        return { previous };
      },
      onError: (err, _id, ctx) => {
        rollback(qc, key, ctx?.previous);
        pushToast(errorMessage(err, "Couldn't remove guest"));
      },
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
    },
  );
}

/**
 * Scan a guest at the door. NOT optimistic (scan is outside the proven-safe set): the
 * server confirms, THEN the row repaints. On success we invalidate so THIS client flips immediately; every
 * OTHER authorized client repaints from the guest-scanned live signal. On error surface it verbatim — never
 * a phantom "arrived".
 */
export function useScanGuest(showId: string) {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const key = guestListKey(bandId, showId);

  return useBandMutation<GuestRow, string>(
    async (band, guestId) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/shows/{showId}/guests/{guestId}/scan",
        { params: { path: { bandId: band, showId, guestId } } },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: key });
      },
      onError: (err) => {
        pushToast(errorMessage(err, "Couldn't scan guest"));
      },
    },
  );
}
